import { useState } from 'react';
import PageHeader from 'src/components/page-header/page-header';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import { useGetInfiniteNews, useGetTags } from './data/queries';
import EmptyPlaceholder from './empty-placeholder';
import NewsCard from './news-card';
import NewsFilters from './news-filters';
import css from './news.module.scss';
import { Skeleton } from '@mui/material';
import NewsTagsFilter from './news-tags-filter';
import { NewsFiltersProps } from './interfaces';
import { PaginationState, TagView } from 'src/types';

export default function News() {
  const skeletons = [...Array(6).keys()].map((i) => (
    <Skeleton key={i} variant='rectangular' sx={{ width: '100%', height: '27rem', borderRadius: '0.5rem' }} />
  ));

  const [filterValues, setFilterValues] = useState<NewsFiltersProps & PaginationState>({
    page: 1,
    limit: 6,
    search: '',
    tags: []
  });

  const {
    data: news = { pages: [] },
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useGetInfiniteNews(filterValues);
  const { data: tags = [] } = useGetTags();

  const [selectedTags, setSelectedTags] = useState<string[]>(tags.map((t) => t.id));

  const handleFiltersChange = (filters: Partial<NewsFiltersProps>) => {
    setFilterValues((prev) => ({ ...prev, ...filters, page: 1 }));
  };

  const allNews = news.pages.flatMap((page) => page.items);
  const isNewsEmpty = allNews.length === 0;
  const hidePaginatedFooter = isFetching || isNewsEmpty || !hasNextPage;

  function getSelectedTags(tag: TagView) {
    if (!tag.name) {
      return [];
    }

    return selectedTags.includes(tag.id) ? selectedTags.filter((tn) => tn !== tag.id) : [...selectedTags, tag.id];
  }

  function handleTagClick(tag: TagView) {
    const newSelectedTags = getSelectedTags(tag);

    setSelectedTags(newSelectedTags);
    setFilterValues((prev) => ({ ...prev, tags: newSelectedTags, page: 1 }));
  }

  return (
    <main className={css.newsPage}>
      <PageHeader
        title='Новини'
        description='Загальні новини та оновлення сервісу'
        filter={
          <div className={css.filters}>
            <NewsFilters initialFilters={filterValues} onSearch={handleFiltersChange} />
          </div>
        }
      />
      <div className={css.container}>
        <NewsTagsFilter tags={tags} onClick={handleTagClick} selectedTags={selectedTags} />
        {!isFetching && isNewsEmpty && <EmptyPlaceholder />}

        <div className={css.cards}>
          {isFetching && skeletons}
          {!isFetching && allNews.map((news) => <NewsCard news={news} key={news.id} />)}
        </div>

        {!hidePaginatedFooter && (
          <div className={css.pagerRow}>
            <PaginatedFooter caption='Більше новин' onClick={() => fetchNextPage()} disabled={isFetchingNextPage} />
          </div>
        )}
      </div>
    </main>
  );
}
